import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useRouter } from 'next/router';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { jsonLdScriptProps } from 'react-schemaorg';
import ComprehensiveListings from '@/components/ComprehensiveListings';
import FAQs from '@/components/FAQs';
import { COMPANY_ADDRESS, COMPANY_INFO, COMPANY_SOCIALS } from '@/constants/company';
import { STICKY_SUB_NAV_POSITION_CLASSES } from '@/constants/stickySubNavPositionClasses';
import { musicFaqContent } from './faq-content';
import { SEO } from '@/components/SEO';
import { SliderSkeleton } from '@/components/Slider/SliderSkeleton';
import { TileCarousel, TileCarouselItem } from '@/components/TileCarousel';
import { Separator } from '@music-vine/cadence/ui';
import { TileContent, TileDescription, TileTitle } from '@/components/ui/tile';
import { CenterpieceSearch } from '@/features/CenterpieceSearch';
import { ZonesNavigation } from '@/features/MusicZones/components/zonesNavigation';
import { useZoneScrollTracking } from '@/hooks/navigation/useZoneScrollTracking';
import { logEvent } from '@/pages/api/v1/utils';
import { SettingsContext } from '@/providers/SettingsProvider';
import { Echo } from '@music-vine/cadence/ui';
import { useTypesenseCarousels } from '@/hooks/useTypesenseCarousels';
import type { Tile } from '@/types/carousel';
import { useQuery } from '@tanstack/react-query';
import isEqual from 'lodash/isEqual';
import type { Organization } from 'schema-dts';
import type { HomePageProps } from './types';
import { getMusicPageCacheKey, getMusicPageData, isPopulatedCarousel } from './util';
import { cn } from '@/lib/utils';

/**
 * Personalised carousels like the Jump Back In and Based on Recent Downloads must show on the 3rd row before the Discover and Seasonal carousels.
 */
const CAROUSELS_INDEX_SEPARATOR = 3;
export const MusicPage: React.FC<HomePageProps> = ({
  initialCacheKey,
  personalisedCarousels: initialPersonalisedCarousels,
  typesenseCarousels: initialTypesenseCarousels,
  comprehensiveListings
}) => {
  const {
    settings
  } = useContext(SettingsContext);
  const cacheKey = getMusicPageCacheKey(settings);
  const {
    asPath,
    isReady
  } = useRouter();
  const stickySentinelRef = useRef<HTMLDivElement>(null);
  const lastIsStickyRef = useRef(false);
  const [isSticky, setIsSticky] = useState(false);
  const carouselListRef = useRef<HTMLDivElement>(null);

  // Track user entering carousel area for hash navigation
  const {
    hasEnteredGrid
  } = useZoneScrollTracking({
    targetRef: carouselListRef
  });

  // Set instant scroll behavior for hash navigation
  useEffect(() => {
    const originalScrollBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    return () => {
      document.documentElement.style.scrollBehavior = originalScrollBehavior;
    };
  }, []);
  const {
    data: personalisedCarousels
  } = useQuery({
    queryKey: cacheKey,
    queryFn: () => getMusicPageData(Number(settings?.user?.id) || undefined),
    initialData: initialPersonalisedCarousels,
    enabled: isReady && !isEqual(cacheKey, initialCacheKey)
  });
  const {
    data: carousels
  } = useTypesenseCarousels({
    searchParameters: {
      q: 'music',
      filter_by: 'page_slug:=music'
    },
    queryOptions: {
      initialData: initialTypesenseCarousels
    }
  });

  // Handle sticky progress for ZonesNavigation (mirrors Zones page behavior)
  useEffect(() => {
    const sentinel = stickySentinelRef.current;
    if (!sentinel) {
      return;
    }
    const handleScroll = () => {
      const sentinelRect = sentinel.getBoundingClientRect();
      const nextIsSticky = sentinelRect.top <= 64;
      if (nextIsSticky !== lastIsStickyRef.current) {
        lastIsStickyRef.current = nextIsSticky;
        setIsSticky(nextIsSticky);
      }
    };

    // Initial calculation
    handleScroll();
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // This is a test feature to see if the feature flag is working
  useFeatureIsOn('a-a-test');
  return <section className="homepage" data-sentry-component="MusicPage" data-sentry-source-file="index.tsx">
      <div className="page-container home-container pb-10">
        <SEO description="Exceptional copyright-free music from real producers, composers and indie talent. All fully cleared for easy music licensing. Sign up and start downloading for free." title="Royalty-Free Music For Video, Content & Film • Uppbeat" data-sentry-element="SEO" data-sentry-source-file="index.tsx">
          <script {...jsonLdScriptProps<Organization>({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: COMPANY_INFO.name,
          url: COMPANY_INFO.url,
          logo: COMPANY_INFO.logo,
          parentOrganization: {
            '@type': 'Organization',
            name: COMPANY_INFO.legalName
          },
          address: {
            '@type': 'PostalAddress',
            streetAddress: COMPANY_ADDRESS.streetAddress,
            addressLocality: COMPANY_ADDRESS.city,
            postalCode: COMPANY_ADDRESS.postalCode,
            addressCountry: COMPANY_ADDRESS.countryCode
          },
          description: 'Uppbeat is a pioneering free music platform for YouTubers and content creators that guarantees no YouTube copyright issues or demonetization of content. Headquartered in Leeds, UK, and owned by Music Vine Limited, Uppbeat launched in 2021 with a curated catalog of top beatmakers and indie artists. The platform now also offers an extensive library of sound effects as well as a leading blog with valuable tips and information for its userbase of over 1.5 million creators.',
          foundingDate: COMPANY_INFO.foundingDate,
          sameAs: [...COMPANY_SOCIALS]
        })} />
        </SEO>

        <CenterpieceSearch variant="music" data-sentry-element="CenterpieceSearch" data-sentry-source-file="index.tsx" />

        {/* 1px observer sentinel */}
        <div className="mx-container h-px" ref={stickySentinelRef} />

        {/* Sticky container for zones navigation */}
        {/* The mobile header is actually 52px tall, but setting the top
            value to 51px prevents a subpixel rendering issue */}
        <div className={cn('sticky z-10', STICKY_SUB_NAV_POSITION_CLASSES)}>
          <div className="bg-white pt-2 dark:bg-gray-950">
            <ZonesNavigation currentZoneSlug="featured" shouldIncludeHash={hasEnteredGrid} data-sentry-element="ZonesNavigation" data-sentry-source-file="index.tsx" />
          </div>
        </div>

        {/* INFO: Placeholder for the next feature banner. */}
        {/* <FeaturedBannerHomePage > */}

        <div className="home-page_carousel-listing pt-4" data-testid="carousel-listing" ref={carouselListRef}>
          {carousels?.length ? <>
              {/* Typesense Carousels Part 1/2*/}
              {carousels.slice(0, CAROUSELS_INDEX_SEPARATOR)?.map(carousel => {
            if (!carousel.tiles?.length) {
              return null;
            }
            return <React.Fragment key={carousel.name}>
                      <div className="mb-1 px-container">
                        <Echo asChild>
                          <h2>{carousel.name}</h2>
                        </Echo>
                      </div>
                      <TileCarousel className="fl-mb-8/10" key={carousel.name}>
                        {carousel.tiles.sort((a, b) => a.order - b.order).map((tile, index) => <TileCarouselItem alt={tile.title} href={tile.slug} index={index} key={tile.slug} onClick={() => {
                  void logEvent({
                    event_type: 'click',
                    asPath,
                    metadata: {
                      type: carousel.name,
                      text: tile.title,
                      next_path: tile.slug
                    }
                  });
                }} src={tile.image} variant={carousel.tile_display_type === 1 ? 'tall' : undefined}>
                              <TileContent visible>
                                <div>
                                  {!!carousel.show_carousel_title && <TileTitle>{tile.title}</TileTitle>}
                                  {!!carousel.show_carousel_description && <TileDescription>
                                      {tile.description}
                                    </TileDescription>}
                                </div>
                              </TileContent>
                            </TileCarouselItem>)}
                      </TileCarousel>
                    </React.Fragment>;
          })}

              {personalisedCarousels?.carousels?.jumpBackIn?.map(carousel => {
            if (!isPopulatedCarousel(carousel)) {
              return null;
            }
            return <React.Fragment key={carousel.name}>
                    <div className="mb-1 px-container">
                      <Echo asChild>
                        <h2>{carousel.name}</h2>
                      </Echo>
                    </div>
                    <TileCarousel className="mb-10" key={carousel.name}>
                      {carousel.tiles.sort((a, b) => a.order - b.order).map((tile: Tile, index: number) => <TileCarouselItem alt={tile.name} href={tile.url} index={index} key={tile.id || tile.url} onClick={() => {
                  void logEvent({
                    event_type: 'click',
                    asPath,
                    metadata: {
                      type: 'carousel_jump_back_in',
                      text: tile.name,
                      next_path: tile.url
                    }
                  });
                }} src={tile.tile_image}>
                            <TileContent visible>
                              <div>
                                <TileTitle>{tile.name}</TileTitle>
                                {/* NOTE: Uncommented instead of deleting to see if SEO will improve as per DEV-3150 */}
                                {/* <TileDescription>
                                    {getDescription(tile)}
                                  </TileDescription> */}
                              </div>
                            </TileContent>
                          </TileCarouselItem>)}
                    </TileCarousel>
                  </React.Fragment>;
          })}

              {personalisedCarousels?.carousels?.basedOnRecentDl?.map(carousel => {
            if (!isPopulatedCarousel(carousel)) {
              return null;
            }
            return <React.Fragment key={carousel.name}>
                      <div className="mb-1 px-container">
                        <Echo asChild>
                          <h2>{carousel.name}</h2>
                        </Echo>
                      </div>
                      <TileCarousel className="mb-10" key={carousel.name}>
                        {carousel.tiles.sort((a, b) => a.order - b.order).map((tile, index) => <TileCarouselItem alt={tile.name ?? ''} artistName={tile.main_track_artist} energy={tile.main_track_energy} href={tile.url} index={index} key={tile.id || tile.url} leftImage={tile.tile_image_left} mainImage={tile.tile_image_main} onClick={() => {
                  void logEvent({
                    event_type: 'click',
                    asPath,
                    metadata: {
                      type: 'carousel_based_on_recent_dl',
                      text: `${tile.main_track_name} - ${tile.main_track_artist}`,
                      next_path: tile.url
                    }
                  });
                }} rightImage={tile.tile_image_right} trackId={tile.main_track_id} trackName={tile.main_track_name} variant="similar">
                              <TileContent visible>
                                <TileTitle>{tile.name ?? ''}</TileTitle>
                                {/* NOTE: Uncommented instead of deleting to see if SEO will improve as per DEV-3150 */}
                                {/* <TileDescription /> */}
                              </TileContent>
                            </TileCarouselItem>)}
                      </TileCarousel>
                    </React.Fragment>;
          })}

              {/* Typesense Carousels Part 2/2*/}
              {carousels.slice(CAROUSELS_INDEX_SEPARATOR)?.map(carousel => {
            if (!carousel.tiles?.length) {
              return null;
            }
            return <React.Fragment key={carousel.name}>
                    <div className="mb-1 px-container">
                      <Echo asChild>
                        <h2>{carousel.name}</h2>
                      </Echo>
                    </div>
                    <TileCarousel className="mb-10" key={carousel.name}>
                      {carousel.tiles.sort((a, b) => a.order - b.order).map((tile, index) => <TileCarouselItem alt={tile.title} href={tile.slug} index={index} key={tile.slug} onClick={() => {
                  void logEvent({
                    event_type: 'click',
                    asPath,
                    metadata: {
                      type: carousel.name,
                      text: tile.title,
                      next_path: tile.slug
                    }
                  });
                }} src={tile.image} variant={carousel.tile_display_type === 1 ? 'tall' : undefined}>
                            <TileContent visible>
                              <div>
                                {!!carousel.show_carousel_title && <TileTitle>{tile.title}</TileTitle>}
                                {!!carousel.show_carousel_description && <TileDescription>
                                    {tile.description}
                                  </TileDescription>}
                              </div>
                            </TileContent>
                          </TileCarouselItem>)}
                    </TileCarousel>
                  </React.Fragment>;
          })}
            </> : <>
              <SliderSkeleton />
              <SliderSkeleton />
              <SliderSkeleton />
            </>}
        </div>
      </div>
      <div className="px-container">
        <ComprehensiveListings initialData={comprehensiveListings} variant="music" data-sentry-element="ComprehensiveListings" data-sentry-source-file="index.tsx" />
      </div>
      <div className="px-container">
        <Separator className="mt-10" data-sentry-element="Separator" data-sentry-source-file="index.tsx" />
        <FAQs faqs={musicFaqContent} variant="inline" data-sentry-element="FAQs" data-sentry-source-file="index.tsx" />
      </div>
    </section>;
};